import { useLocation } from 'react-router';
import queryString from 'query-string';
import { useDispatch } from 'react-redux';
import { useUser } from '../useUser';
import { useEffect, useState } from 'react';
import { trackPageView } from '../../support-and-tracking/analytics-actions';
import { changeUnactivatedEmail } from '../user-actions';
import LoaderBar from '../../components/loader-bar/LoaderBar';
import { ContentTitle } from '../../components/typography/Heading';
import { isValidEmail, userIsActivated } from '../user-model';
import VerifyWithBankIdButtons from '../../components/button/verify-with-bankid-buttons/VerifyWithBankIDButtons';
import RoundedButtonPrimary from '../../components/button/rounded-button-primary/RoundedButtonPrimary';
import ButtonText from '../../components/button/button-text/ButtonText';
import Input2 from '../../components/input/input-2/Input2';
import ButtonWrapper from '../../components/button/button-wrapper/ButtonWrapper';
import { startEmailChangeVerification } from '../verification-actions';
import styles from './edit-user-email.module.less';

const VerifyEmailChange = ({ newEmail, onAbort, onSuccess = () => {} }) => {
    const location = useLocation();
    const query = queryString.parse(location.search);
    const isComplete = query?.isComplete ?? false;
    const dispatch = useDispatch();
    const user = useUser();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isFetchingUser = user.isFetching;

    useEffect(() => {
        trackPageView({ urlAsEvent: 'verify-user-request' });
    }, []);

    const handleBankIdVerification = (e) => {
        e.preventDefault();
        dispatch(startEmailChangeVerification({ newEmail, redirectUrl: location.pathname }))
            .then((url) => {
                window.location.href = url;
            })
            .catch(() => {});
    };

    const handleEmailChange = () => dispatch(changeUnactivatedEmail({ newEmail })).then(() => onSuccess());
    if (!user || isFetchingUser) {
        return <LoaderBar />;
    }

    if (isComplete) {
        return (
            <div>
                <ContentTitle tag="h1">Nå må du bekrefte e-posten din</ContentTitle>
                <p>
                    For at Spleis-kontoen din skal fungere som normalt så må du bekrefte at du har mottatt en e-post
                    fra. Det gjør du ved å klikke på lenken vi har sendt deg på e-post.
                </p>
                <p>
                    Den nye e-postadressen vi har registrert på deg er: <em>{newEmail}</em>
                </p>
            </div>
        );
    }

    const isActivated = userIsActivated(user);

    return (
        <form
            className={styles.container}
            onSubmit={(e) => {
                setIsSubmitting(true);
                e.preventDefault();
                if (!isActivated) {
                    handleEmailChange(e);
                }
            }}
        >
            <p>
                E-postadressen din endres fra <em>{user.email}</em> til <em>{newEmail}</em>
            </p>

            {isActivated ? (
                <>
                    <VerifyWithBankIdButtons handleBankIdVerification={handleBankIdVerification} />
                    <ButtonWrapper>{onAbort && <ButtonText onClick={onAbort}>Tilbake</ButtonText>}</ButtonWrapper>
                </>
            ) : (
                <ButtonWrapper>
                    <RoundedButtonPrimary disabled={isSubmitting}>Bekreft endring</RoundedButtonPrimary>
                    {onAbort && (
                        <ButtonText disabled={isSubmitting} onClick={onAbort}>
                            Tilbake
                        </ButtonText>
                    )}
                </ButtonWrapper>
            )}
        </form>
    );
};

const EditUserEmail = ({ onSuccess, onAbort }) => {
    const user = useUser();
    const [newEmail, setNewEmail] = useState(user?.email || '');
    const [shouldVerifyEmailChange, setShouldVerifyEmailChange] = useState(false);
    const [emailError, setEmailError] = useState(null);

    const abortEmailChange = () => {
        setShouldVerifyEmailChange(false);
    };

    if (shouldVerifyEmailChange) {
        return <VerifyEmailChange newEmail={newEmail} onAbort={abortEmailChange} onSuccess={onSuccess} />;
    }

    const continueWithNewEmail = (e) => {
        e.preventDefault();
        if (user.email === newEmail) {
            setEmailError('E-postadressen er allerede lagret på brukeren');
        } else if (!isValidEmail(newEmail)) {
            setEmailError('E-posten er ikke gyldig');
        } else {
            setShouldVerifyEmailChange(true);
        }
    };

    return (
        <form onSubmit={continueWithNewEmail} className={styles.container}>
            <Input2
                fieldName="email"
                autoComplete="email"
                autoCapitalize="none"
                autoFocus
                title="Din e-post"
                fieldDescription=""
                type="email"
                value={newEmail}
                error={emailError}
                required
                onChange={(e) => setNewEmail(e.target.value)}
                wrapperClass={styles.inputWrapper}
            />
            <ButtonWrapper>
                <RoundedButtonPrimary>Gå videre</RoundedButtonPrimary>
                {onAbort && <ButtonText onClick={onAbort}>Avbryt</ButtonText>}
            </ButtonWrapper>
        </form>
    );
};

export default EditUserEmail;
